import { motion } from "framer-motion";
import { CalendarDays, Clock, MapPin, Users, Building2 } from "lucide-react";
import { EVENT, IMAGES } from "@/lib/siteData";
import { StiftungChance } from "@/components/site/StiftungChance";

const fade = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export const EventDetails = () => {
  return (
    <section id="anlass" className="relative z-20 -mt-16 bg-ink px-4 sm:px-6 pb-28" data-testid="event-details-section">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {/* Big feature card */}
          <motion.div
            variants={fade} custom={0} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="relative overflow-hidden rounded-3xl border border-white/10 md:col-span-7 min-h-[320px]"
          >
            <img src={IMAGES.swissKids} alt="Fröhliche Kinder in der Schweiz" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-end p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-cyan mb-3">Ein Abend für den guten Zweck</p>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-ivory max-w-md leading-tight">
                Inspirierende Begegnungen, die etwas Positives bewirken.
              </h2>
            </div>
          </motion.div>

          {/* Date / time stack */}
          <motion.div
            variants={fade} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:col-span-5 flex flex-col justify-between gap-8"
          >
            <div className="flex items-start gap-4">
              <span className="mt-1 rounded-full bg-magenta/15 p-3 text-magenta"><CalendarDays size={20} /></span>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ivory/40 mb-1">Datum</p>
                <p className="font-display text-2xl sm:text-3xl text-ivory leading-tight">{EVENT.date}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="mt-1 rounded-full bg-cyan/15 p-3 text-cyan"><Clock size={20} /></span>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ivory/40 mb-1">Beginn</p>
                <p className="font-display text-2xl sm:text-3xl text-ivory leading-tight">{EVENT.time}</p>
              </div>
            </div>
          </motion.div>

          {/* Location */}
          <motion.div
            variants={fade} custom={2} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:col-span-5"
          >
            <span className="inline-flex rounded-full bg-violet/15 p-3 text-violet mb-5"><MapPin size={20} /></span>
            <p className="text-xs uppercase tracking-[0.25em] text-ivory/40 mb-1">Ort</p>
            <p className="font-display text-2xl text-ivory leading-tight">{EVENT.location}</p>
            <p className="text-ivory/60 mt-1">{EVENT.city}</p>
          </motion.div>

          {/* Host */}
          <motion.div
            variants={fade} custom={3} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:col-span-4"
          >
            <span className="inline-flex rounded-full bg-cyan/15 p-3 text-cyan mb-5"><Building2 size={20} /></span>
            <p className="text-xs uppercase tracking-[0.25em] text-ivory/40 mb-1">Veranstalter</p>
            <p className="font-display text-2xl text-ivory leading-tight">{EVENT.host}</p>
          </motion.div>

          {/* Beneficiary */}
          <motion.div
            variants={fade} custom={4} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="rounded-3xl border border-white/10 bg-gradient-to-br from-violet/20 to-magenta/10 backdrop-blur-xl p-8 md:col-span-3"
          >
            <span className="inline-flex rounded-full bg-magenta/20 p-3 text-magenta mb-5"><Users size={20} /></span>
            <p className="text-xs uppercase tracking-[0.25em] text-ivory/40 mb-1">Begünstigt</p>
            <p className="font-display text-xl text-ivory leading-tight">
              <StiftungChance testid="event-stiftung-link" className="decoration-magenta/60" />
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
